/*
  Higher Class Glass — Cart Recommendation Algorithm
*/

// ─── RECOMMENDATION MAP ───────────────────────────────────────────────────────
const RECOMMENDATION_MAP = {
  WaterPipe:            ["Slide", "AshCatcher"],
  DabRig:               ["Banger"],
  Banger:               ["DabRig"],
  Slide:                ["WaterPipe"],
  AshCatcher:           ["WaterPipe"],
  HandPipe:             ["Grinder"],
  Grinder:              ["HandPipe"],
  Vaporizer:            ["VaporizerAttachment"],
  VaporizerAttachment:  ["Vaporizer"],
  Misc:                 ["Misc"],
};

const FALLBACK_TAG = "Misc";

let cachedRecommendation = null;
let lastCartFingerprint = null;


// ─── FETCH CART (with tags) ──────────────────────────────────────────────────
async function fetchCart() {
  const response = await fetch("/cart.js");
  const cart = await response.json();

  await Promise.all(cart.items.map(async (item) => {
    try {
      const res = await fetch(`/products/${item.handle}.js`);
      const product = await res.json();
      item.tags = Array.isArray(product.tags)
        ? product.tags.join(",")
        : (product.tags || "");
    } catch (e) {
      item.tags = "";
    }
  }));

  return cart;
}


// ─── HELPERS ──────────────────────────────────────────────────────────────────
function getMostExpensiveItem(cartItems) {
  return cartItems.reduce((mostExpensive, item) => {
    return item.price > mostExpensive.price ? item : mostExpensive;
  }, cartItems[0]);
}

function getProductTypeTag(itemTags) {
  if (!itemTags) return null;
  const tagList = itemTags.split(",").map(tag => tag.trim());
  return tagList.find(tag => RECOMMENDATION_MAP.hasOwnProperty(tag)) || null;
}

function getCartTypeTags(cartItems) {
  const typeTags = new Set();
  cartItems.forEach(item => {
    const tag = getProductTypeTag(item.tags || "");
    if (tag) typeTags.add(tag);
  });
  return typeTags;
}

async function fetchProductsByTag(tag) {
  const response = await fetch(`/collections/all/products.json?limit=250`);
  const data = await response.json();
  return data.products.filter(product => {
    const tags = Array.isArray(product.tags)
      ? product.tags.map(t => t.trim())
      : (product.tags || "").split(",").map(t => t.trim());
    return tags.includes(tag);
  });
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}


// ─── BUILD RECOMMENDATION HTML ───────────────────────────────────────────────
function buildRecommendationHTML(product) {
  const variantId = product.variants[0].id;
  const price = parseFloat(product.variants[0].price).toFixed(2);

  // Try multiple image sources for compatibility
  let image = "";
  if (product.images && product.images.length > 0) {
    image = typeof product.images[0] === "string"
      ? product.images[0]
      : (product.images[0].src || "");
  } else if (product.featured_image) {
    image = product.featured_image;
  }

  return `
    <div style="
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 16px;
      margin: 20px 0;
      display: flex;
      align-items: center;
      gap: 16px;
      background: #fafafa;
    ">
      ${image ? `<img src="${image}" alt="${product.title}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 4px;" onerror="this.style.display='none'">` : ""}
      <div style="flex: 1;">
        <p style="margin: 0 0 4px 0; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #888;">
          You might also need
        </p>
        <p style="margin: 0 0 4px 0; font-weight: bold;">
          ${product.title}
        </p>
        <p style="margin: 0 0 12px 0; color: #555;">
          $${price}
        </p>
        <button
          onclick="addRecommendationToCart(${variantId})"
          style="
            background: #000;
            color: #fff;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          "
        >
          Add to Cart
        </button>
      </div>
    </div>
  `;
}


// ─── RENDER INTO ALL CONTAINERS ──────────────────────────────────────────────
function renderIntoContainers(product) {
  const containers = document.querySelectorAll("#cart-recommendation");
  if (!containers.length) return;
  const html = buildRecommendationHTML(product);
  containers.forEach(container => {
    container.innerHTML = html;
  });
}

function clearContainers() {
  const containers = document.querySelectorAll("#cart-recommendation");
  containers.forEach(container => {
    container.innerHTML = "";
  });
}


// ─── ADD RECOMMENDATION TO CART ──────────────────────────────────────────────
async function addRecommendationToCart(variantId) {
  await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  });
  cachedRecommendation = null;
  lastCartFingerprint = null;
  window.location.reload();
}


// ─── MAIN FUNCTION ────────────────────────────────────────────────────────────
async function runCartRecommendation() {
  const cart = await fetchCart();

  // Build a fingerprint of the cart so we know if it changed
  const fingerprint = cart.items.map(i => `${i.product_id}x${i.quantity}`).join("|");

  // If cart is empty, clear recommendations and reset
  if (!cart.items || cart.items.length === 0) {
    cachedRecommendation = null;
    lastCartFingerprint = null;
    clearContainers();
    return;
  }

  // If cart hasn't changed, just re-render the cached recommendation
  if (fingerprint === lastCartFingerprint && cachedRecommendation) {
    renderIntoContainers(cachedRecommendation);
    return;
  }

  lastCartFingerprint = fingerprint;

  const cartItems = cart.items;
  const cartTypeTags = getCartTypeTags(cartItems);
  const topItem = getMostExpensiveItem(cartItems);
  const topItemTag = getProductTypeTag(topItem.tags || "");
  const primaryTag = topItemTag || FALLBACK_TAG;
  const suggestedTags = RECOMMENDATION_MAP[primaryTag] || [FALLBACK_TAG];

  let chosenTag = null;
  for (const tag of suggestedTags) {
    if (tag === FALLBACK_TAG) { chosenTag = FALLBACK_TAG; break; }
    if (!cartTypeTags.has(tag)) { chosenTag = tag; break; }
  }

  if (!chosenTag) chosenTag = FALLBACK_TAG;

  let candidates = await fetchProductsByTag(chosenTag);

  // Always exclude items already in the cart (by product id)
  const cartProductIds = new Set(cartItems.map(item => item.product_id));
  candidates = candidates.filter(p => !cartProductIds.has(p.id));

  // If no candidates found for chosen tag, fall back to Misc
  if (candidates.length === 0 && chosenTag !== FALLBACK_TAG) {
    candidates = await fetchProductsByTag(FALLBACK_TAG);
    candidates = candidates.filter(p => !cartProductIds.has(p.id));
  }

  if (candidates.length === 0) {
    clearContainers();
    return;
  }

  cachedRecommendation = pickRandom(candidates);
  renderIntoContainers(cachedRecommendation);
}


// ─── MUTATION OBSERVER ────────────────────────────────────────────────────────
// Watches for new #cart-recommendation divs appearing and refills them.
// Also watches for cart item changes (add/remove) to trigger a refresh.
let refreshTimeout = null;
const observer = new MutationObserver(() => {
  // Refill empty drawer divs from cache immediately
  if (cachedRecommendation) {
    const containers = document.querySelectorAll("#cart-recommendation");
    containers.forEach(container => {
      if (!container.innerHTML.trim()) {
        container.innerHTML = buildRecommendationHTML(cachedRecommendation);
      }
    });
  }

  // Debounce a full refresh to detect cart changes
  if (refreshTimeout) clearTimeout(refreshTimeout);
  refreshTimeout = setTimeout(runCartRecommendation, 800);
});

observer.observe(document.body, { childList: true, subtree: true });


// ─── RUN ON PAGE LOAD ─────────────────────────────────────────────────────────
document.addEventListener("DOMContentLoaded", runCartRecommendation);

// Run when cart drawer trigger is clicked
document.addEventListener("click", function(e) {
  const cartIcon = e.target.closest('[data-testid="cart-drawer-trigger"]');
  if (cartIcon) {
    setTimeout(runCartRecommendation, 600);
  }
});